'use client';

import * as React from 'react';
import Downshift from 'downshift';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AutoCompleteProps<T> extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onSelect' | 'size'
> {
  errored?: boolean;
  icon?: React.ReactNode;
  selectedItem?: T | null;
  isLoading?: boolean;
  keyFn: (item: T) => string;
  displayFn: (item: T) => string;
  onSelect: (selected: T | null) => void;
  itemToString?: (item: T | null) => string;
  onInputChange?: (value: string) => void;
  filterFn: (inputValue: string | null) => T[];
  size?: 'default' | 'xs' | 'sm';
  mode?: 'local' | 'server';
}

export const AutoComplete = <T,>(props: AutoCompleteProps<T>) => {
  const {
    icon,
    size = 'default',
    keyFn,
    errored,
    filterFn,
    onSelect,
    displayFn,
    isLoading,
    itemToString,
    onInputChange,
    selectedItem,
    className,
    disabled,
    mode = 'local',
    ...rest
  } = props;

  const id = React.useId();
  const prevValueRef = React.useRef(rest.value as string | undefined);

  React.useEffect(() => {
    if (mode === 'server' && rest.value !== prevValueRef.current) {
      prevValueRef.current = rest.value as string | undefined;
    }
  }, [mode, rest.value]);

  return (
    <div className="flex flex-col space-y-1.5 w-full">
      <Downshift
        itemToString={itemToString || ((item) => (item ? displayFn(item) : ''))}
        onSelect={(selected) => onSelect(selected)}
        selectedItem={selectedItem}
        onInputValueChange={(value) => {
          if (value === undefined) return;
          onInputChange?.(value);
        }}
        inputValue={rest.value as string}
      >
        {({
          isOpen,
          inputValue,
          getRootProps,
          getMenuProps,
          getItemProps,
          getInputProps,
          clearSelection,
          highlightedIndex,
          getToggleButtonProps,
        }) => (
          <div className="relative w-full">
            <div {...getRootProps({}, { suppressRefError: true })} className="relative group">
              <input
                {...getInputProps({
                  ...rest,
                })}
                id={id}
                disabled={disabled}
                className={cn(
                  'flex w-full rounded-xl border bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 shadow-sm dark:bg-black',
                  size === 'xs'
                    ? 'h-8 py-1 text-xs'
                    : size === 'sm'
                      ? 'h-9 py-1.5 text-sm'
                      : 'h-11',

                  // Padding for icons and buttons
                  icon && 'pl-10',
                  selectedItem ? 'pr-20' : 'pr-10',

                  // States
                  errored
                    ? 'border-destructive focus-visible:ring-destructive'
                    : 'border-input hover:border-accent group-focus-within:border-primary',
                  className
                )}
              />

              {icon && (
                <div
                  className={cn(
                    'absolute left-3.5 flex items-center justify-center text-muted-foreground/70 group-focus-within:text-primary transition-colors duration-200',
                    size === 'xs' || size === 'sm'
                      ? 'top-1/2 -translate-y-1/2'
                      : 'top-1/2 -translate-y-1/2'
                  )}
                >
                  {icon}
                </div>
              )}

              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/50" />}

                {selectedItem && !disabled && (
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => {
                      onSelect(null);
                      clearSelection();
                    }}
                    className="flex items-center justify-center w-7 h-7 hover:bg-muted text-muted-foreground hover:text-foreground transition-all duration-150 rounded-lg hover:rotate-90"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}

                <button
                  {...getToggleButtonProps()}
                  type="button"
                  tabIndex={-1}
                  disabled={disabled}
                  className="flex items-center justify-center w-7 h-7 hover:bg-muted text-muted-foreground hover:text-foreground transition-all duration-150 rounded-lg"
                >
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-200',
                      isOpen && 'rotate-180'
                    )}
                  />
                </button>
              </div>
            </div>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 5, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute z-50 w-full"
                >
                  <ol
                    {...getMenuProps()}
                    className={cn(
                      'max-h-64 w-full overflow-y-auto rounded-xl border bg-background/95 backdrop-blur-md p-1 shadow-xl ring-1 ring-black/5 focus:outline-none',
                      size === 'xs' ? 'text-xs' : size === 'sm' ? 'text-sm' : 'text-base'
                    )}
                  >
                    {(() => {
                      const items = filterFn(inputValue);
                      if (items.length === 0) {
                        return (
                          <li className="px-4 py-8 text-center text-muted-foreground flex flex-col items-center gap-2">
                            <span className="text-xl">🔍</span>
                            <span>No results found</span>
                          </li>
                        );
                      }

                      return items.map((item, index) => (
                        <li
                          key={keyFn(item)}
                          {...getItemProps({
                            item,
                            index,
                            tabIndex: -1,
                          })}
                          className={cn(
                            'relative flex cursor-default select-none items-center rounded-lg py-2 px-3 outline-none transition-colors duration-150 mb-0.5 last:mb-0',
                            index === highlightedIndex
                              ? 'bg-accent text-accent-foreground cursor-pointer translate-x-1 border-l-4 border-primary pl-2'
                              : 'text-foreground hover:bg-muted/50',
                            selectedItem &&
                              keyFn(selectedItem) === keyFn(item) &&
                              'bg-primary/5 font-medium text-primary',
                            size === 'xs' && 'py-1.5 px-2.5',
                            size === 'sm' && 'py-1.5 px-2.5'
                          )}
                        >
                          <span className="break-words whitespace-normal">{displayFn(item)}</span>
                        </li>
                      ));
                    })()}
                  </ol>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </Downshift>
    </div>
  );
};
